import { Button, FormControl, Icon, InputLabel, makeStyles, MenuItem, Select, Theme } from '@material-ui/core';
import { useModeFormStyle } from '@moderepo/mode-app-components';
import { AttributeConditionConjunction, AttributeValueCompareOperator, Entity, EntityAttributeParam, EntityAttributeValueType }
    from '@moderepo/mode-internal-apis';
import clsx from 'clsx';
import React, { CSSProperties, useCallback, useState, useEffect, useMemo } from 'react';
import { AttributeConditionInput, AttributeConditionInputProps, AttributeFilterSettings, EntityClassesFilterInput, EntityFilterInput,
    EntityFilterSettings, FilterArraySettings, FilterSettings, GenericFilterInput } from '.';
import { ARRAY_SUPPORTED_OPERATORS, AttributesQuery, AttributesQueryCondition } from '../../containers';
import { NonEmptyArray } from '../../utils/utils';


const useStyle = makeStyles((theme: Theme) => {

    return {
        root: {
            padding      : theme.spacing(1),
            display      : 'flex',
            flexDirection: 'column',
        },


        attributeInputsContainer: {
            flex          : 1,
            width         : '100%',
            display       : 'flex',
            flexDirection : 'row',
            alignItems    : 'center',
            justifyContent: 'space-between',
            marginBottom  : theme.spacing(1),
        },

        attributeInput: {
        },

        parentFilterContainer: {
            display       : 'flex',
            flexDirection : 'row',
            justifyContent: 'flex-start',
            alignItems    : 'center',
        },

        [theme.breakpoints.up('md')]: {
            root: {
                padding: theme.spacing(2),
            },
            attributeInputsContainer: {
                marginBottom: theme.spacing(1.5),
            },
        },

        [theme.breakpoints.up('lg')]: {
            root: {
                padding: theme.spacing(3),
            },
            attributeInputsContainer: {
                marginBottom: theme.spacing(2),
            },
        },
    };

}, { name: 'EntitiesSearchOptions', index: 1 });


// Maximum number of attribute the user can filter by
const MAX_ATTRIBUTE_FILTERS = 5;


// Use this as the default operator since all attribute value comparison can use this operator
const DEFAULT_OPERATOR = AttributeValueCompareOperator.EXACT_EQUAL;


/**
 * When the user selected a different operator, we MIGHT need to reset the values because the existing values might not be valid
 * anymore. If we user changed from an operator that support ARRAY to an operator that DOES NOT support ARRAY then we will need to
 * reset the inputs and vise versa. So this function will help check if we need to create a new values array or continue using the
 * previous one.
 */
const getNewConditionValues = (
    previousOperator: AttributeValueCompareOperator | undefined,
    previousValues: NonEmptyArray<EntityAttributeValueType | undefined>,
    newOperator: AttributeValueCompareOperator,
): NonEmptyArray<EntityAttributeValueType | undefined> => {

    if (previousOperator !== undefined) {
        // If previous operator support multiple values but the new operator does not, return a new array containing just the FIRST element
        // from the previousValues.
        if (ARRAY_SUPPORTED_OPERATORS.includes(previousOperator) && !ARRAY_SUPPORTED_OPERATORS.includes(newOperator)) {
            return [previousValues[0]];
        }

        // If the previous operator DOES NOT support multiple values but the new operator DOES, we can continue using the previousValues
        // array. However, the previousValues array has only 1 element so we can add 1 more element to the array
        if (!ARRAY_SUPPORTED_OPERATORS.includes(previousOperator) && ARRAY_SUPPORTED_OPERATORS.includes(newOperator)) {
            return [...previousValues, undefined];
        }

        // If we get here, that mean it is either BOTH previous and new operators support multiple values or NEITHER support multiple
        // values. They are kind of the same type of operator therefore we can continue using the previousValues.
        return previousValues;
    }

    // We need to create a new values array. We need to decide whether to create array of 1 values or 2 values

    // If the newOperator is an operator that supports multiple values, prepare at least 2 values so that the user doesn't have to click "OR"
    // to add more. We can assume the user will want to provide at least 2 values because if the user choose an operator that support multiple
    // values, there is no reason for them to provide only 1 value. If they only want to provide 1 value then they should have chosen one of the
    // other operators.
    if (ARRAY_SUPPORTED_OPERATORS.includes(newOperator)) {
        return [undefined, undefined];
    }

    return [undefined];
};


export interface EntitiesFiltersConfigPanelProps {
    readonly className?: string | undefined;
    readonly style?: CSSProperties | undefined;

    // To show homeId filter option. This filter is only needed if homeId is not preselected.
    readonly homeIdFilterSettings?: FilterSettings<number> | undefined;

    // To show entityClass filter option.
    readonly entityClassesFilterSettings?: FilterArraySettings<string> | undefined;

    // To show entity attribute filter option.
    readonly attributesFilterSettings?: AttributeFilterSettings<EntityAttributeValueType> | undefined;

    // To show Parent filter option.
    readonly parentFilterSettings?: {
        readonly entity: EntityFilterSettings;
        readonly recursive: FilterSettings<number>;
    } | undefined;

    readonly texts: {
        readonly applyFiltersButtonLabel: string;
        readonly closeFilterButtonLabel: string;
        readonly emptyInputMessage: string;
        readonly addAttributeFilterButtonLabel: string;
        readonly removeAttributeFilterButtonLabel: string;
        readonly conjunctionInputLabel: string;
        readonly andConjunctionValue: string;
        readonly orConjunctionValue: string;
    } & AttributeConditionInputProps['texts'],


    /**
     * Callback handler for when one of the search/filters input changed. This DOES NOT mean to notify the handler to apply the filters,
     * it is only to let the handler know some settings changed so that the handler can do what it needs to to.
     */
    readonly onFilterChanged: (filters: {
        readonly homeIdFilter?: number | undefined;
        readonly entityClassesFilter?: readonly string[] | undefined;
        readonly attributesFilter?: AttributesQuery | undefined;
        readonly parentFilter?: {
            readonly entity: Entity | undefined;
            readonly recursive: number | undefined;
        } | undefined;
    })=> void;


    // Callback handler to handle the search/filter when the user click on the "Apply" button. The handler will be provided with the
    // filter values the user selected, homeId, or entityClass, or both if both options are enabled.
    readonly onApplySearchAndFilters: (filters: {
        // Note: value 'undefined' means the option is NOT enabled and the user didn't selected any value. It is NOT the same
        // as empty value e.g. 0 or ''.
        readonly homeIdFilter?: number | undefined;
        readonly entityClassesFilter?: readonly string[] | undefined;
        readonly attributesFilter?: AttributesQuery | undefined;

        // 'parentFilter` will be undefined if the option is not enabled. If it is enabled, it will be an object with 2 fields, entity
        // and recursive. If the option is enabled BUT an entity is not selected, then 'entity' will be undefined and recursive will also be
        // undefined.
        readonly parentFilter?: {
            readonly entity: Entity | undefined;
            readonly recursive: number | undefined;
        } | undefined;
    })=> void;

    // Handler for when the 'close' button is clicked
    readonly onCloseSearchAndFilters: ()=> void;
}


/**
 * This component is displaying all the filter options and getting the user to choose the filter options.
 */
export const EntitiesFiltersConfigPanel: React.FC<EntitiesFiltersConfigPanelProps> = (props: EntitiesFiltersConfigPanelProps) => {

    const [selectedHomeId, setSelectedHomeId] = useState<number | undefined>(props.homeIdFilterSettings?.currentValue);
    const [selectedEntityClasses, setSelectedEntityClasses] = useState<readonly string[] | undefined>(
        props.entityClassesFilterSettings?.currentValue,
    );
    const [attributesQuery, setAttributesQuery] = useState<AttributesQuery>({
        conjunction: AttributeConditionConjunction.AND,
        conditions : [],
    });
    const [selectedParent, setSelectedParent] = useState<Entity | undefined>(props.parentFilterSettings?.entity.currentValue);
    const [recursive, setRecursive] = useState<number | undefined>(props.parentFilterSettings?.recursive.currentValue);

    const formClasses = useModeFormStyle();
    const classes = useStyle();

    // Get all the filter settings from props.
    const { homeIdFilterSettings, entityClassesFilterSettings, parentFilterSettings, attributesFilterSettings } = props;


    // When the user select a different home, the "homeId" set in the "parentFilterSettings" is no longed should be used
    // Therefore this useMemo will update the "parentFilterSettings" with the new homeId.
    const updatedParentFilterSettings = useMemo((): EntityFilterSettings | undefined => {
        if (parentFilterSettings) {
            return {
                ...parentFilterSettings.entity,
                entitiesListingContainerProps: {
                    ...parentFilterSettings.entity.entitiesListingContainerProps,
                    homeId: selectedHomeId,
                },
            };
        }

        return undefined;
    }, [parentFilterSettings, selectedHomeId]);


    /**
     * Because the attributesQuery's condition the user already set up is based on the set of filterable attributes. Therefore, if these
     * options changed, we will need to clear all the current attributesQuery's conditions. This can happen when the user select/unselect
     * entity classes. The attributes options will be based on the selected entityClasses and the options can change when the list of
     * selected entity classes change.
     */
    useEffect(() => {
        if (attributesFilterSettings?.attributeOptions) {
            setAttributesQuery((currentQuery) => {
                // Return a copy of the previous attributesQuery and replace the conditions with a new array
                return {
                    ...currentQuery,
                    conditions: [],
                };
            });
        }
    }, [attributesFilterSettings?.attributeOptions]);



    /**
     * On "Apply" search and filter button handler.
     */
    const onApplySearchAndFilters = useCallback(async (event: React.FormEvent) => {
        event.stopPropagation();
        event.preventDefault();

        props.onApplySearchAndFilters({
            homeIdFilter       : selectedHomeId,
            entityClassesFilter: selectedEntityClasses,
            attributesFilter   : attributesQuery,
            parentFilter       : props.parentFilterSettings ? {
                entity   : selectedParent,
                // if a parent is NOT selected then 'recursive' option has no meaning therefore set it to undefined
                // Else use whatever state it is currently set to
                recursive: selectedParent ? recursive : undefined,
            } : undefined,
        });
    }, [attributesQuery, props, recursive, selectedParent, selectedEntityClasses, selectedHomeId]);



    /**
     * On ADD condition to the query
     */
    const onAddAttributeCondition = useCallback(() => {
        setAttributesQuery((currentQuery) => {
            // return a copy of the current query and ADD a new condition to the array of conditions
            return {
                ...currentQuery,
                conditions: [
                    ...currentQuery.conditions,
                    {
                        attribute: undefined,
                        operator : DEFAULT_OPERATOR,
                        values   : [undefined],
                    },
                ],
            };
        });
    }, []);


    const onConjunctionChanged = useCallback((event: React.ChangeEvent<{ value: unknown }>) => {
        setAttributesQuery((currentQuery) => {
            // return a copy of the current query and ADD a new condition to the array of conditions
            return {
                ...currentQuery,
                conjunction: (event.target.value as AttributeConditionConjunction),
            };
        });
    }, []);


    /**
     * On REMOVE condition from the query
     */
    const onRemoveAttributeCondition = useCallback((indexToRemove: number) => {
        setAttributesQuery((currentQuery) => {
            // return a copy of the current query and REMOVE the condition at 'indexToRemove'
            return {
                ...currentQuery,
                conditions: currentQuery.conditions.filter((_, index) => {
                    return index !== indexToRemove;
                }),
            };
        });
    }, []);



    const onHomeIdChanged = useCallback((homeId: number | undefined) => {
        setSelectedHomeId(homeId);

        // When the user selected a different home, we can't keep the current selected Parent anymore because it is possible that
        // the parent doesn't belong to the new selected home. Therefore, we have to clear the parent input.
        setSelectedParent(undefined);
        setRecursive(undefined);

        if (props.onFilterChanged) {
            props.onFilterChanged({
                homeIdFilter: homeId,
            });
        }
    }, [props]);


    const onEntityClassesChanged = useCallback((entityClasses: readonly string[] | undefined) => {
        setSelectedEntityClasses(entityClasses);
        if (props.onFilterChanged) {
            props.onFilterChanged({
                entityClassesFilter: entityClasses,
            });
        }
    }, [props]);



    const onParentChanged = useCallback((parent: Entity | undefined) => {
        setSelectedParent(parent);

        // If the user unselected the parent then automatically unselect `recursive` as well because this option is pointless without
        // a parent entity selected
        if (!parent) {
            setRecursive(undefined);
        }

        if (props.onFilterChanged) {
            props.onFilterChanged({
                parentFilter: {
                    entity: parent,
                    recursive,
                },
            });
        }
    }, [props, recursive]);


    const onRecursiveChanged = useCallback((recursive: number | undefined) => {
        setRecursive(recursive);

        if (props.onFilterChanged) {
            props.onFilterChanged({
                parentFilter: {
                    entity: selectedParent,
                    recursive,
                },
            });
        }
    }, [props, selectedParent]);


    /**
     * On 'attribute' input changed for the attributeCondition at 'index'. We will update the condition at that 'index'
     */
    const onAttributeChanged = useCallback((newAttribute: EntityAttributeParam<EntityAttributeValueType>, index: number) => {
        setAttributesQuery((currentQuery) => {

            // Get the attributeCondition at the 'index'
            const attributeCondition = currentQuery.conditions[index];
            if (attributeCondition) {

                // Create a new attribute condition object and initialize it with the selected 'attribute'.
                // When the user selected a different attribute, reset the operator and value because the operator and value might not be
                // valid anymore. Different type of attribute will have different type of operator and value therefore we need to clear the old one.
                // For example, lets say the previous attribute is type STRING and the operator was '==' (for substring match) and the value was
                // a string. Then the user change the attribute to another attribute of type NUMBER. The previous operator, '==', can't be used
                // for number and the value can't be used either because it was a string.
                const newCondition: AttributesQueryCondition = {
                    attribute: newAttribute,
                    operator : DEFAULT_OPERATOR,
                    values   : [undefined],
                };

                // Create a new attributes query so that it will trigger a re-render. This new attributesQuery is just a copy of the old
                // attributesQuery except it's condition at 'index' has been replaced
                const newAttributesQuery = {
                    ...currentQuery,
                    conditions: [...currentQuery.conditions],
                };
                newAttributesQuery.conditions[index] = newCondition;

                // Notify handler
                if (props.onFilterChanged) {
                    props.onFilterChanged({
                        attributesFilter: newAttributesQuery,
                    });
                }

                return newAttributesQuery;
            }

            // Nothing changed, return the same object
            return currentQuery;
        });
    }, [props]);



    /**
     * On 'operator' input changed for the attributeCondition at 'index'. We will update the attributeCondition at that 'index'
     */
    const onOperatorChanged = useCallback((newOperator: AttributeValueCompareOperator, index: number) => {
        setAttributesQuery((currentQuery) => {

            // Get the attributeCondition at the 'index'
            const attributeCondition = currentQuery.conditions[index];
            if (attributeCondition) {

                const newValues = getNewConditionValues(attributeCondition.operator, attributeCondition.values, newOperator);

                // Create a new attribute condition object by making a copy of the previous condition and replace the 'operator'
                // with the new selected operator.
                const newCondition: AttributesQueryCondition = {
                    ...attributeCondition,
                    operator: newOperator,
                    values  : newValues,
                };


                // Create a new attributes query so that it will trigger a re-render. This new attributesQuery is just a copy of the old
                // attributesQuery except it's condition at 'index' has been replaced
                const newAttributesQuery = {
                    ...currentQuery,
                    conditions: [...currentQuery.conditions],
                };
                newAttributesQuery.conditions[index] = newCondition;

                // Notify handler
                if (props.onFilterChanged) {
                    props.onFilterChanged({
                        attributesFilter: newAttributesQuery,
                    });
                }

                return newAttributesQuery;
            }

            // Nothing changed, return the same object
            return currentQuery;
        });
    }, [props]);



    /**
     * On 'values' input changed for the attributeCondition at 'index'. We will update the attributeCondition at that 'index'
     */
    const onValuesChanged = useCallback((values: NonEmptyArray<EntityAttributeValueType | undefined>, index: number) => {
        setAttributesQuery((currentQuery) => {

            // Get the attributeCondition at the 'index'
            const attributeCondition = currentQuery.conditions[index];
            if (attributeCondition) {
                // Create a new AttributeCondition object by copying the current AttributeCondition and replace the 'values' prop with the
                // new 'values'
                const newCondition: AttributesQueryCondition = {
                    ...attributeCondition,
                    values,
                };

                // Create a new attributes query so that it will trigger a re-render. This new attributesQuery is just a copy of the old
                // attributesQuery except it's condition at 'index' has been replaced
                const newAttributesQuery = {
                    ...currentQuery,
                    conditions: [...currentQuery.conditions],
                };
                newAttributesQuery.conditions[index] = newCondition;

                // Notify handler
                if (props.onFilterChanged) {
                    props.onFilterChanged({
                        attributesFilter: newAttributesQuery,
                    });
                }

                return newAttributesQuery;
            }

            // Nothing changed, return the same object
            return currentQuery;
        });
    }, [props]);



    /**
     * Validate all attributes conditions to make sure they are valid
     */
    const isAttributesQueryConditionsValid = useMemo(() => {
        // Go through each condition and find 1 condition that is invalid
        const invalidCondition = attributesQuery.conditions.find((condition) => {
            // condition is valid if the `attribute` is selected, an 'operator' is selected, and all 'values' are provided
            const hasAllInputs = condition.attribute !== undefined
                    && condition.operator !== undefined
                    && !condition.values.includes(undefined);

            // Because we are supposed to find an INVALID condition therefore we need to return TRUE if the condition DOES NOT have all inputs.
            return !hasAllInputs;
        });

        // Return TRUE if there is no invalidCondition
        return invalidCondition === undefined;
    }, [attributesQuery]);



    return (
        <div className={clsx(props.className, classes.root)} style={props.style}>
            <div className={clsx(formClasses.root)}>
                <div className={clsx(formClasses.formInputGroup, 'medium')}>

                    {/* Create the UI for the Home ID filter if it is configured */}
                    {homeIdFilterSettings && (
                        <GenericFilterInput
                            filterSettings={homeIdFilterSettings}
                            currentValue={selectedHomeId}
                            setCurrentValue={onHomeIdChanged}
                        />
                    )}

                    {/* Create the UI for the Entity Class filter if it is configured */}
                    {entityClassesFilterSettings && (
                        <EntityClassesFilterInput
                            filterSettings={entityClassesFilterSettings}
                            currentValue={selectedEntityClasses}
                            setCurrentValue={onEntityClassesChanged}
                            emptyInputMessage={props.texts.emptyInputMessage}
                        />
                    )}

                    {/*
                        Create the UI for the Parent filter if it is configured. The `parent` input always goes with the `recursive` input
                        therefore we put them inside a container
                    */}
                    {parentFilterSettings && updatedParentFilterSettings && (
                        <div className={classes.parentFilterContainer}>
                            <EntityFilterInput
                                currentValue={selectedParent}
                                filterSettings={updatedParentFilterSettings}
                                setCurrentValue={onParentChanged}
                                emptyInputMessage={props.texts.emptyInputMessage}
                            />
                            <GenericFilterInput
                                disabled={selectedParent === undefined}
                                filterSettings={parentFilterSettings.recursive}
                                currentValue={Number(recursive ?? false)}
                                setCurrentValue={onRecursiveChanged}
                            />
                        </div>
                    )}
                </div>

                {/* Create the UI for the Attribute filter if it is configured */}
                {attributesFilterSettings && (
                    <>
                        {/*
                            If there are at least 2 condition then we can start showing the conjunction input. If there is 1 condition,
                            it doesn't matter whether the conjunction is AND or OR, the result should be the same. If there is 0 condition
                            then there is no need to show the conjunction.
                        */}
                        {attributesQuery.conditions.length > 1 && (
                            <div className={clsx(classes.attributeInputsContainer)}>
                                <FormControl
                                    variant="outlined"
                                    className={clsx(classes.input, classes.valueInput)}
                                >
                                    <InputLabel>{props.texts.conjunctionInputLabel}</InputLabel>
                                    <Select
                                        label={props.texts.conjunctionInputLabel}
                                        value={attributesQuery.conjunction}
                                        displayEmpty
                                        onChange={onConjunctionChanged}
                                    >
                                        <MenuItem value={AttributeConditionConjunction.AND}>
                                            {props.texts.andConjunctionValue}
                                        </MenuItem>
                                        <MenuItem value={AttributeConditionConjunction.OR}>
                                            {props.texts.orConjunctionValue}
                                        </MenuItem>
                                    </Select>
                                </FormControl>
                            </div>
                        )}

                        {attributesQuery?.conditions.map((filter, index) => {
                            return (
                                <div
                                    className={clsx(classes.attributeInputsContainer)}
                                    // eslint-disable-next-line react/no-array-index-key
                                    key={index}
                                >
                                    <AttributeConditionInput
                                        className={clsx(classes.attributeInput)}
                                        attributeOptions={attributesFilterSettings.attributeOptions}
                                        operatorOptions={attributesFilterSettings.operatorOptions}
                                        texts={{
                                            ...props.texts,
                                            attributeNameLabel: attributesFilterSettings.label,
                                        }}
                                        attributeQueryCondition={filter}
                                        onAttributeChanged={(attribute) => {
                                            onAttributeChanged(attribute, index);
                                        }}
                                        onOperatorChanged={(attribute) => {
                                            onOperatorChanged(attribute, index);
                                        }}
                                        onValuesChanged={(attribute) => {
                                            onValuesChanged(attribute, index);
                                        }}
                                    />
                                    <Button
                                        variant="contained"
                                        color="secondary"
                                        size="small"
                                        onClick={() => {
                                            onRemoveAttributeCondition(index);
                                        }}
                                    >
                                        {props.texts.removeAttributeFilterButtonLabel}
                                    </Button>
                                </div>
                            );
                        })}
                    </>
                )}


                <div className={clsx(formClasses.formActionContainer)}>
                    <div>
                        {attributesFilterSettings && attributesFilterSettings.attributeOptions.length > 0 && (
                            <Button
                                variant="contained"
                                color="primary"
                                size="small"
                                disabled={attributesQuery && attributesQuery.conditions.length >= MAX_ATTRIBUTE_FILTERS}
                                startIcon={<Icon>add</Icon>}
                                onClick={onAddAttributeCondition}
                            >
                                {props.texts.addAttributeFilterButtonLabel}
                            </Button>
                        )}
                    </div>
                    <div>
                        <Button
                            className={clsx(formClasses.formActionButton)}
                            color="secondary"
                            size="small"
                            onClick={props.onCloseSearchAndFilters}
                        >
                            {props.texts.closeFilterButtonLabel}
                        </Button>
                        <Button
                            className={clsx(formClasses.formActionButton)}
                            variant="contained"
                            color="primary"
                            size="small"
                            onClick={onApplySearchAndFilters}
                            disabled={!isAttributesQueryConditionsValid}
                        >
                            {props.texts.applyFiltersButtonLabel}
                        </Button>
                    </div>
                </div>
            </div>
        </div>
    );
};
